import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { getFormValid } from 'utils/formUtils';
import FormLabelWithField from 'components/Core/FormLabelWithField';
import Button from 'components/Core/Button';
import Select from 'components/Core/Select';
import NumberInputField from 'components/Core/NumberInputField';
import EmbedForm from '../../EmbeddableForm';
import EmbedLink from '../../EmbedLink';

const RoofShingleCalcForm = ({ pathUrl, onSubmit, show, ...otherProps }) => {
  const pitchOptions = Array.from({ length: 24 }, (_, i) => ({
    label: `${i + 1}/12`,
    value: `${i + 1}/12`,
  }));

  const onCalc = ({ roofWidth, roofLength, roofPitch }) => {
    const [rise, run] = roofPitch.split('/').map(Number);
    const pitchFactor = Math.sqrt(rise ** 2 + run ** 2) / run;
    // Calculate the footprint area and roof area including slope
    const footprintArea = roofWidth * roofLength;
    const roofArea = footprintArea * pitchFactor;
    // Calculate roof area in squares (1 square = 100 sq ft)
    const roofAreaInSquares = roofArea / 100;
    // Calculate number of shingle bundles and round up to the nearest whole number
    const shingleBundles = Math.ceil(roofAreaInSquares * 3);
    // Calculate the number of individual shingles (assuming 29 shingles per bundle)
    const shingles = shingleBundles * 29;
    onSubmit({ roofArea, shingles, shingleBundles });
  };

  const formik = useFormik({
    initialValues: {
      roofLength: 100,
      roofWidth: 100,
      roofPitch: pitchOptions[0].value,
    },
    validationSchema: Yup.object().shape({
      roofLength: Yup.number().required('Roof Length is required'),
      roofWidth: Yup.number().required('Roof Width is required'),
      roofPitch: Yup.string().required('Ratio is required'),
    }),
    onSubmit: (_values) => {
      onCalc(_values);
    },
  });

  const onHandleChange = (id, newValue) => {
    formik.setFieldTouched(id);
    formik.setFieldValue(id, newValue);
  };

  const formValid = getFormValid({ ...formik });

  return (
    <div className={`w-full h-full absolute top-0 ${show ? 'z-10' : 'z-0'}`}>
      <div id="roof_shingle_calc_form">
        <div
          className={`transition-all duration-500 ${
            show ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
          data-position="roofing area form"
        >
          <div className="mb-[21px]">
            <FormLabelWithField label="Roof Length (Feet)">
              <NumberInputField
                id="roofLength"
                min={0}
                max={999999999999}
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
              />
            </FormLabelWithField>
          </div>
          <div className="mb-[21px]">
            <FormLabelWithField label="Roof Width (Feet)">
              <NumberInputField
                id="roofWidth"
                min={0}
                max={999999999999}
                values={formik.values}
                formValid={formValid}
                handleChange={onHandleChange}
              />
            </FormLabelWithField>
          </div>
          <div className="mb-[21px]">
            <FormLabelWithField label="Roof Pitch (X/12)">
              <Select
                id="roofPitch"
                values={formik.values}
                options={pitchOptions}
                formValid={formValid}
                handleChange={onHandleChange}
                defaultValue={pitchOptions[0].value}
              />
            </FormLabelWithField>
          </div>
        </div>
      </div>
      <div className={`absolute bottom-0 w-full ${show ? 'visible' : 'invisible'}`}>
        <Button
          className="!w-full"
          type="Purple"
          shadow={false}
          onClick={() => formik.submitForm()}
        >
          Calculate
        </Button>
        <div className="form-embed-link">
          <EmbedLink
            show={!otherProps?.isEmbed}
            openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
            label="Add tool to your website"
            pathUrl={pathUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default EmbedForm(RoofShingleCalcForm);

export const RoofShingleCalcResult = ({ result }) => (
  <div className="flex flex-col items-center self-stretch gap-[20px] md:gap-[40px]">
    <h4 className="text-[16px] font-black text-[var(--grey-8)] leading-[110%] tracking-[0.8px] uppercase mb-0">
      Estimated Results
    </h4>
    <div className="w-full flex flex-col items-center">
      <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
        Roof Area:
      </p>
      <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
        {result.roofArea?.toFixed(2)}
        &nbsp;ft²
      </h3>
    </div>
    <div className="w-full flex flex-col items-center">
      <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
        Number of Shingles:
      </p>
      <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
        {result.shingles}
        &nbsp;squares
      </h3>
      <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
        (The number of individual shingles)
      </p>
    </div>
    <div className="w-full flex flex-col items-center">
      <p className="text-[16px] text-black text-center font-normal leading-[150%] mb-0">
        Bundles of Shingles:
      </p>
      <h3 className="text-[28px] text-black font-extrabold leading-[120%] tracking-[-1.12px] mb-0">
        {result.shingleBundles}
      </h3>
      <p className="text-[12px] text-black font-normal leading-[120%] mb-0">
        (based on a typical bundle of 29)
      </p>
    </div>
  </div>
);
